/**
 * WLS Cart Listener - Intercepts Shopify Cart API calls and provides event handling
 * 
 * This module provides a clean API for listening to all Shopify cart operations
 * including add, update, change, clear, and cart fetch operations.
 * 
 * @author Wollson development team
 * @email development@wollson.co
 * @version 1.0.0
 */

// ============================================================================
// MAIN CART LISTENER OBJECT
// ============================================================================

/**
 * Main WLS Cart object that provides cart event listening functionality
 */
window.WLS_CART = {
    
    /**
     * Cart API URL constants for Shopify endpoints
     * These are the actual API endpoints that Shopify uses for cart operations
     * @type {Object}
     */
    API_URLS: {
        ADD: '/cart/add',      // Add items to cart
        UPDATE: '/cart/update', // Update cart attributes/notes
        CHANGE: '/cart/change', // Change item quantities
        CLEAR: '/cart/clear',   // Clear entire cart
        CART: '/cart.js'        // Fetch current cart state
    },
    
    /**
     * Creates a cart event listener that intercepts fetch calls to cart endpoints
     * @param {Function} callback - Function to call when cart events occur
     * @param {Array} events - Array of API URLs to listen for (default: all)
     * @param {boolean} debug - Enable debug logging (default: false)
     * @returns {Function} Unsubscribe function to stop listening
     */
    EVENT_LISTENER: function(callback, events = Object.values(this.API_URLS), debug = false) {
        
        // Store reference to original fetch function
        const originalFetch = window.fetch;
        
        // ========================================================================
        // VALIDATION
        // ========================================================================
        
        // Validate that provided events are valid API URLs
        const validUrls = Object.values(this.API_URLS);
        const filteredEvents = events.filter(event => validUrls.includes(event));
        
        if (filteredEvents.length === 0) {
            console.error('WLS_CART: No valid events provided. Valid events:', validUrls);
            return function() {}; // Return empty unsubscribe function
        }
        
        if (debug) {
            console.log('WLS_CART: Listening to events:', filteredEvents);
        }
        
        // ========================================================================
        // FETCH INTERCEPTION
        // ========================================================================
        
        /**
         * Override the global fetch function to intercept cart API calls
         * This allows us to capture all cart-related network requests
         */
        window.fetch = function(...args) {
            const [url, options] = args;
            
            // Only process string URLs (not Request objects)
            if (typeof url === 'string') {
                
                // Check if this URL matches any of the events we're listening for
                const matchesEvent = filteredEvents.some(event => {
                    return url.includes(event);
                });
                
                if (matchesEvent) {
                    if (debug) {
                        console.log('WLS_CART: Cart API call detected:', url);
                    }
                    
                    // Execute the original fetch request
                    return originalFetch.apply(this, args)
                        .then(response => {
                            
                            // Only process successful responses
                            if (response.ok) {
                                
                                // Clone the response so we can read it without consuming it
                                const clonedResponse = response.clone();
                                
                                // Parse the JSON response
                                clonedResponse.json().then(data => {
                                    if (debug) {
                                        console.log('WLS_CART: Cart API response:', data);
                                    }
                                    
                                    // Call the user's callback function if provided
                                    if (typeof callback === 'function') {
                                        
                                        // Create event info object with response data and metadata
                                        const eventInfo = {
                                            data: data,                    // Cart API response data
                                            url: url,                     // Original URL that was called
                                            event: filteredEvents.find(event => url.includes(event)) // Which event was triggered
                                        };
                                        
                                        callback(eventInfo);
                                    }
                                }).catch(err => {
                                    if (debug) {
                                        console.log('WLS_CART: Error parsing cart response:', err);
                                    }
                                });
                            }
                            
                            // Return the original response for the calling code
                            return response;
                        });
                }
            }
            
            // For non-cart URLs, use the original fetch function
            return originalFetch.apply(this, args);
        };
        
        // ========================================================================
        // CLEANUP FUNCTION
        // ========================================================================
        
        /**
         * Returns a function that restores the original fetch function
         * Call this to stop listening to cart events
         * 
         * How unsubscribe works:
         * 1. When you call EVENT_LISTENER, it replaces window.fetch with our custom function
         * 2. The original fetch function is stored in 'originalFetch' variable
         * 3. The returned function restores window.fetch back to the original function
         * 4. This stops our cart interception and restores normal fetch behavior
         * 
         * @returns {Function} Function to call when you want to stop listening
         */
        return function() {
            // Restore the original fetch function, removing our cart interception
            window.fetch = originalFetch;
        };
    }
};

// ============================================================================
// EXAMPLE USAGE
// ============================================================================

/*
// Example 1: Listen to all cart events
window.WLS_CART.EVENT_LISTENER((eventInfo) => {
  console.log('Cart changed:', eventInfo.data);
  console.log('Event type:', eventInfo.event);
});

// Example 2: Listen only to add events
window.WLS_CART.EVENT_LISTENER((eventInfo) => {
  console.log('Product added:', eventInfo.data);
  console.log('Event type:', eventInfo.event); // '/cart/add'
}, [window.WLS_CART.API_URLS.ADD], true);

// Example 3: Listen to multiple specific events
window.WLS_CART.EVENT_LISTENER((eventInfo) => {
  if (eventInfo.event === window.WLS_CART.API_URLS.ADD) {
    console.log('Product added:', eventInfo.data);
  } else if (eventInfo.event === window.WLS_CART.API_URLS.CHANGE) {
    console.log('Quantity changed:', eventInfo.data);
  } else if (eventInfo.event === window.WLS_CART.API_URLS.CLEAR) {
    console.log('Cart cleared:', eventInfo.data);
  }
}, [
  window.WLS_CART.API_URLS.ADD, 
  window.WLS_CART.API_URLS.CHANGE, 
  window.WLS_CART.API_URLS.CLEAR
], true);

// Example 4: Stop listening to events
const unsubscribe = window.WLS_CART.EVENT_LISTENER((eventInfo) => {
  console.log('Cart changed:', eventInfo.data);
});

// Later, stop listening (this restores the original fetch function)
unsubscribe();

// Example 5: How unsubscribe works step by step
console.log('Before listener:', window.fetch); // Original fetch function

const unsubscribe2 = window.WLS_CART.EVENT_LISTENER((eventInfo) => {
  console.log('Cart changed:', eventInfo.data);
});

console.log('After listener:', window.fetch); // Our custom fetch function

unsubscribe2(); // Call this to stop listening

console.log('After unsubscribe:', window.fetch); // Back to original fetch function
*/